import React, { useState, useEffect, useMemo } from 'react';
import { AuditEntry, AuditFilters } from '../types';
import { getAuditLog } from '../api/client';
import { Search, Download, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

interface AuditLogProps {
  onRefresh?: () => void;
}

const statusBadges: Record<string, string> = {
  approved: 'bg-mantis-50 text-mantis-700 border-mantis-200',
  rejected: 'bg-redwood-50 text-redwood-700 border-redwood-200',
  pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
  executing: 'bg-blue-50 text-blue-700 border-blue-200',
  failed: 'bg-redwood-50 text-redwood-700 border-redwood-200',
};

export const AuditLog: React.FC<AuditLogProps> = () => {
  const [logs, setLogs] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filters
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<string>('all');
  const [workstream, setWorkstream] = useState<string>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const filters: AuditFilters = {
        workstream: workstream !== 'all' ? workstream : undefined,
        status: status !== 'all' ? status : undefined,
        search: search.trim() || undefined,
      };
      const data = await getAuditLog(filters);
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading audit log:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    setCurrentPage(1);
  }, [workstream, status]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLogs();
    setCurrentPage(1);
  };

  const safeLogs = Array.isArray(logs) ? logs : [];

  // Local client filtering for instant search
  const filteredLogs = useMemo(() => {
    if (!search.trim()) return safeLogs;
    const q = search.toLowerCase();
    return safeLogs.filter((log) => {
      const agent = (log.agentName ?? '').toLowerCase();
      const type = (log.actionType ?? '').toLowerCase();
      const reviewer = (log.reviewer ?? '').toLowerCase();
      const desc = (log.description ?? '').toLowerCase();
      return agent.includes(q) || type.includes(q) || reviewer.includes(q) || desc.includes(q);
    });
  }, [safeLogs, search]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, currentPage, pageSize]);

  const exportCSV = () => {
    if (filteredLogs.length === 0) return;
    const headers = ['ID', 'Timestamp', 'Action Type', 'Agent', 'Reviewer', 'Status'];
    const rows = filteredLogs.map((l) => [
      l.id ?? '',
      l.timestamp ?? '',
      `"${l.actionType ?? ''}"`,
      `"${l.agentName ?? ''}"`,
      `"${l.reviewer ?? 'System'}"`,
      l.status ?? '',
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `audit_log_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4">
      {/* Controls Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search action, agent, reviewer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-mantis-500"
          />
        </form>

        {/* Filters & Export */}
        <div className="flex items-center space-x-2 w-full sm:w-auto justify-between sm:justify-end">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-white border border-slate-200 text-xs text-slate-800 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-mantis-500"
          >
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="pending_review">Pending</option>
          </select>

          <select
            value={workstream}
            onChange={(e) => setWorkstream(e.target.value)}
            className="bg-white border border-slate-200 text-xs text-slate-800 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-mantis-500"
          >
            <option value="all">All Workstreams</option>
            <option value="marketing">Marketing</option>
            <option value="referrals">Referrals</option>
            <option value="intake">Intake</option>
            <option value="scheduling">Scheduling</option>
            <option value="rcm">RCM</option>
            <option value="hr">HR</option>
            <option value="finance">Finance</option>
            <option value="clinical-ops">Clinical Ops</option>
          </select>

          <button
            onClick={exportCSV}
            disabled={filteredLogs.length === 0}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Simple Audit Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-xs animate-pulse">
            Loading governance trail...
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-xs">
            No governance records found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-200 bg-slate-50 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-4">Timestamp</th>
                  <th className="py-2.5 px-4">Action & Agent</th>
                  <th className="py-2.5 px-4">Reviewer</th>
                  <th className="py-2.5 px-4">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {paginatedLogs.map((log) => {
                  const badgeClass = statusBadges[log.status] ?? 'bg-slate-100 text-slate-600 border-slate-200';
                  return (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      {/* Timestamp */}
                      <td className="py-3 px-4 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</span>
                        </div>
                      </td>

                      {/* Action & Agent */}
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-800">{log.actionType ?? 'Action'}</div>
                        <div className="text-[11px] text-slate-500">
                          Agent: <span className="font-medium text-slate-700">{log.agentName ?? 'System'}</span>
                        </div>
                      </td>

                      {/* Reviewer */}
                      <td className="py-3 px-4 text-slate-600">
                        {log.reviewer ? (
                          <span className="font-medium">{log.reviewer}</span>
                        ) : (
                          <span className="text-slate-400 italic">Automated / System</span>
                        )}
                      </td>

                      {/* Result / Status */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${badgeClass}`}>
                          {(log.status ?? 'pending').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {!loading && filteredLogs.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            <div>
              Showing {Math.min((currentPage - 1) * pageSize + 1, filteredLogs.length)} to{' '}
              {Math.min(currentPage * pageSize, filteredLogs.length)} of {filteredLogs.length} entries
            </div>

            <div className="flex items-center space-x-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-medium text-slate-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="p-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
